import React from 'react';

// assets

// services
import ValidationService from '../../../../../../services/validation';
import GeneralService from '../../../../../../services/general';

// props

// components
import { ActionButton, Checkbox, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Stack, TagPicker, TextField } from '@fluentui/react';
import Label from '../../../../../typography/label';
import Text from '../../../../../typography/text';
import { InsuranceCostFormData } from '.';
import VendorsService from '../../../../../../services/data/vendors';
import VendorField from '../../../../../uiframeworks/forms/vendor';
import { PPhPercentageOptions, PPNPercentageOptions } from '../../../../../../manifests/taxPercentage';
import PricingService from '../../../../../../services/general/pricing';
import { IOutcomePricingProps } from '../../../../../../props/general/price';

type InsuranceCostingFormProps = {
    data?: Partial<InsuranceCostFormData>;
    onDismissed(): void;
    onSubmit(data: InsuranceCostFormData): void;
}

const InsuranceCostingForm: React.FC<InsuranceCostingFormProps> = (props: InsuranceCostingFormProps) => {
    const [error, setError] = React.useState<any>({});
    const [data, setData] = React.useState<Partial<InsuranceCostFormData>>(props.data ? {
        ...props.data,
        price: Number(props.data?.price) + "",
        totalPrice: Number(props.data?.price) + "",
        goodsValue: Number(props.data?.goodsValue) + "",
        percentage: Number(props.data?.percentage) + ""
    } : {
        id: GeneralService.guid(),
        name: '',
        priceType: 'percentage',
        goodsValue: '',
        percentage: '1',
        price: '',
        totalPrice: '',
        notes: ''
    });

    const isSubmitButtonDisabled = () => {
        return false;
    }

    const calculateTotalPrice = (_data: Partial<InsuranceCostFormData>) => {
        let totalPrice = Number(_data.price);
        return totalPrice.toFixed(2);
    }

    const calculatePrice = (_data: Partial<InsuranceCostFormData>) => {
        let price = 0;

        if (_data.priceType === 'percentage' && (_data.goodsValue || "").trim() !== '' && (_data.percentage || "").trim() !== '') {
            return (Number(_data.goodsValue) * Number(_data.percentage) / 100).toFixed(2);
        } else if (_data.priceType === 'custom') {
            return _data.price;
        }

        return price.toFixed(2);
    }

    const calculateAllPrice = (_data: Partial<InsuranceCostFormData>) => {
        let pricing: IOutcomePricingProps = {
            price: _data.totalPrice || "0",
            ppn: _data.ppn || "0",
            pph: _data.pph || "0",
            includePPn: _data.includePPn || false,
            includePPh: _data.includePPh || false,
            ppnPercentage: _data.ppnPercentage || "0",
            pphPercentage: _data.pphPercentage || "0",
            finalPrice: _data.finalPrice || "0",
            nettPrice: _data.nettPrice || "0",
            invoicePrice: _data.invoicePrice || "0"
        }

        pricing = PricingService.outcome.calculate(pricing);
        setData({ ..._data, ...pricing, price: data.price });
    }

    return <Panel headerText={`${props.data ? 'Update' : 'Add'} Insurance`}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed()}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                <PrimaryButton text={props.data ? "Update" : "Add"} disabled={isSubmitButtonDisabled()} onClick={() => props.onSubmit(data as InsuranceCostFormData)} />
                <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed() }} />
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack.Item>
                <VendorField required
                    selected={data.vendor}
                    allowCreate
                    onChange={(vendor) => {
                        data.vendor = vendor;
                        setData({ ...data });
                    }} />
            </Stack.Item>
            <Stack.Item>
                <TextField label={"Goods name"}
                    value={data.name}
                    required={true}
                    onChange={(evt, value) => {
                        data.name = value || "";

                        const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                        error.name = validation.message;

                        setData({ ...data });
                        setError({ ...error });
                    }}
                    errorMessage={error.name} />
            </Stack.Item>
            <Stack.Item>
                <TextField label={"Notes"}
                    value={data.notes}
                    multiline={true}
                    rows={1}
                    autoAdjustHeight
                    resizable={false}
                    onChange={(evt, value) => {
                        data.notes = value || "";

                        const validation = ValidationService.combination(value, ['limit'], { maxChars: 1000 });
                        error.notes = validation.message;

                        setData({ ...data });
                        setError({ ...error });
                    }}
                    errorMessage={error.notes} />
            </Stack.Item>
            <Stack className={'divider'}></Stack>
            <Stack.Item>
                <Dropdown label={"Price type"}
                    selectedKey={data.priceType}
                    options={[
                        { key: 'percentage', text: 'Percentage' },
                        { key: 'custom', text: 'Custom' }
                    ]}
                    required={true}
                    onChange={(evt, opt) => {
                        data.priceType = opt ? opt.key as any : 'percentage';
                        data.price = calculatePrice(data);
                        data.totalPrice = calculateTotalPrice(data);

                        setData({ ...data });
                        calculateAllPrice(data);
                    }} />
            </Stack.Item>
            <Stack.Item>
                <TextField label={"Goods price"}
                    value={data.goodsValue}
                    required={true}
                    prefix={'Rp'}
                    onChange={(evt, value) => {
                        if ((value || "").trim() === "" || !isNaN(Number(value))) {
                            data.goodsValue = value || "";
                            data.price = calculatePrice(data);
                            data.totalPrice = calculateTotalPrice(data);

                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 20 });
                            error.goodsValue = validation.message;

                            setData({ ...data });
                            calculateAllPrice(data);
                            setError({ ...error });
                        }
                    }}
                    errorMessage={error.goodsValue} />
            </Stack.Item>
            <Stack horizontal tokens={{ childrenGap: 20 }}>
                {data.priceType === 'percentage' ? <Stack styles={{root: {width: 100}}}>
                    <TextField label={"Percentage"}
                        value={data.percentage}
                        required={true}
                        suffix={'%'}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.percentage = value || "";
                                data.price = calculatePrice(data);
                                data.totalPrice = calculateTotalPrice(data);

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 20 });
                                error.percentage = validation.message;

                                setData({ ...data });
                                calculateAllPrice(data);
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.percentage} />
                </Stack> : null}
                <Stack grow={1}>
                    <TextField label={"Price"}
                        value={data.priceType === 'percentage' ? GeneralService.getNumberWithSeparator(Number(data.price || '0')) : data.price}
                        required={true}
                        prefix={'Rp'}
                        disabled={data.priceType === 'percentage'}
                        onChange={(evt, value) => {
                            if ((value || "").trim() === "" || !isNaN(Number(value))) {
                                data.price = value || "";
                                data.price = calculatePrice(data);
                                data.totalPrice = calculateTotalPrice(data);

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 20 });
                                error.price = validation.message;

                                setData({ ...data });
                                calculateAllPrice(data);
                                setError({ ...error });
                            }
                        }}
                        errorMessage={error.price} />
                </Stack>
            </Stack>
            <Stack className={'divider'}></Stack>
            <Stack tokens={{ childrenGap: 20 }} horizontal>
                <Stack.Item>
                    <Checkbox checked={data.includePPn}
                        label='Include PPn'
                        onChange={(ev, checked) => {
                            data.includePPn = checked;
                            setData({ ...data });
                            calculateAllPrice(data);
                        }} />
                </Stack.Item>
                <Stack.Item>
                    <Checkbox checked={data.includePPh}
                        label='Include PPh'
                        onChange={(ev, checked) => {
                            data.includePPh = checked;
                            setData({ ...data });
                            calculateAllPrice(data);
                        }} />
                </Stack.Item>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'}>PPn</Label>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item>
                        <Dropdown options={PPNPercentageOptions}
                            selectedKey={data.ppnPercentage}
                            onChange={(evt, opt) => {
                                if (opt && opt.key !== 'custom') {
                                    data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                }
                                data.ppnPercentage = (opt?.key || "") as string;

                                setData({ ...data });
                                calculateAllPrice(data);
                            }}
                            styles={{ root: { width: 100 } }} />
                    </Stack.Item>
                    <Stack.Item grow={1}>
                        <TextField required={true}
                            value={data.ppn}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.ppn = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.ppn = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            errorMessage={error.ppn}
                            disabled={data.ppnPercentage !== 'custom'} />
                    </Stack.Item>
                </Stack>
            </Stack>
            <Stack tokens={{ childrenGap: 2 }}>
                <Label size={'xsmall'}>PPh</Label>
                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    <Stack.Item>
                        <Dropdown options={PPhPercentageOptions}
                            selectedKey={data.pphPercentage}
                            onChange={(evt, opt) => {
                                if (opt && opt.key !== 'custom') {
                                    data.pph = (Number(opt.key) / 100 * Number(data.totalPrice)).toFixed();
                                }
                                data.pphPercentage = (opt?.key || "") as string;

                                setData({ ...data });
                                calculateAllPrice(data);
                            }}
                            styles={{ root: { width: 100 } }} />
                    </Stack.Item>
                    <Stack.Item grow={1}>
                        <TextField required={true}
                            value={data.pph}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.pph = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.pph = validation.message;

                                    setData({ ...data });
                                    setError({ ...error });
                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            errorMessage={error.pph}
                            disabled={data.pphPercentage !== 'custom'} />
                    </Stack.Item>
                </Stack>
            </Stack>
            <Stack className={'divider'}></Stack>
            <Stack.Item>
                <TextField label={"Final price"}
                    value={GeneralService.getNumberWithSeparator(Number(data.finalPrice))}
                    disabled={true}
                    prefix={'Rp'}
                    errorMessage={error.quantity} />
            </Stack.Item>
        </Stack>
    </Panel>
};

export default InsuranceCostingForm;
